import { Trans, useLingui } from "@lingui/react/macro";
import React, {
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useState
} from "react";
import { match, P } from "ts-pattern";
import clsx from "clsx";
import { AutocompleteOptions } from "@algolia/autocomplete-core";
import { useRefinementList, UseRefinementListProps } from "react-instantsearch";
import { BarCheckbox, Button } from "@silversea-ssc/ui-components-react";
import { buildAlgoliaClient } from "components/Algolia/ConfiguredInstantSearch";
import { RenderListItem } from "../RefinementListFilter/RefinedListValues";
import {
  buildSources,
  FacetSource,
  facetSources,
  PublicFacetSource
} from "./buildSources";
import { useAutocomplete } from "./useAutocomplete";
import { cruisesLabel } from "src/utils/cruise";
import { useAutocompleteSearch } from "./useAutocompleteSearch";
import { useEnableMobileKeyboardUX } from "./useEnableMobileKeyboardUX";
import { facetLabel, tagItem, TagItem } from "./tagItem";
import {
  addTagTracking,
  removeTagTracking,
  useNoResultsTracking
} from "./tracking";
import Tags from "./Tags";
import * as styles from "./Autocomplete.module.scss";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import {
  localizedFacetSource,
  unlocalizedFacetSource
} from "src/components/Algolia/utils";
import { msg } from "@lingui/core/macro";
import { isPresent } from "src/utils/checks";

type AutocompleteItem = RenderListItem;

type Props = Partial<AutocompleteOptions<AutocompleteItem>> & {
  indexName: string;
  dataContentName: string;
  enabledSources?: readonly PublicFacetSource[];
  detachedMode?: boolean;
  emptyPlaceholder?: string;
  className?: string;
  panelClassName?: string;
  backgroundColor?: "white" | "titanium-dark" | "neutral05";
};

// NOTE: If we add or change an option we must review
// some rules in CSS that use data-input-state attribute
type InputState = "full" | "focused" | "empty";

// eslint-disable-next-line import/no-unused-modules
export const Autocomplete: React.FC<Props> = ({
  indexName,
  dataContentName,
  enabledSources = facetSources,
  detachedMode = false,
  className,
  panelClassName,
  backgroundColor = "neutral05",
  ...props
}) => {
  const { i18n, t } = useLingui();
  const typeHere = t`Type here`;
  const language = useLanguage();
  const [focused, setFocused] = useState(false);
  const searchClient = buildAlgoliaClient();
  useSourceRefinementList({
    attribute: localizedFacetSource(language, "destinationNames"),
    operator: "or"
  });
  useSourceRefinementList({
    attribute: localizedFacetSource(language, "countryNames"),
    operator: "and"
  });
  useSourceRefinementList({
    attribute: "portNames",
    operator: "and"
  });
  useSourceRefinementList({
    attribute: localizedFacetSource(language, "cruiseTagsTree.level1"),
    operator: "or"
  });

  const search = useAutocompleteSearch();
  const hasTags = search.tags.length > 0;

  const sources = buildSources({
    enabledSources,
    searchClient,
    indexName,
    addFacetFilter: onAddTag,
    currentFacetsFilters: search.currentFacetsFilters,
    language
  });
  const {
    autocomplete,
    state: autocompleteState,
    hasResults
  } = useAutocomplete({
    ...props,
    shouldPanelOpen: () => true,
    getSources: () => sources
  });

  useEffect(() => {
    if (!detachedMode) return;

    requestInputOpen();
  }, [detachedMode]);

  const noResults = !hasResults();

  function onAddTag(source: FacetSource, value: string) {
    addTagTracking(tagItem(source, value));
    search.addFacetFilter(source, value);
  }

  const onRemoveTag = async (tag: TagItem) => {
    removeTagTracking(tag);
    search.removeFacetFilter(tag.originalSource, tag.originalLabel);

    if (autocompleteState.isOpen) {
      if (!detachedMode) inputRef.current?.focus();
      await autocomplete.refresh();
    }
  };

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.value == "" && e.key == "Backspace" && hasTags) {
      const lastTag = search.tags[search.tags.length - 1];
      search.removeFacetFilter(lastTag.originalSource, lastTag.label);
    } else {
      inputProps.onKeyDown(e);
    }
  };

  const onInputFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setFocused(true);
    e.currentTarget.placeholder = typeHere;
  };
  const onInputBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setFocused(false);
    if (hasTags) return;
    e.currentTarget.placeholder = props.placeholder || "";
  };

  const requestInputOpen = () => {
    if (!detachedMode) inputRef.current?.focus();
    autocomplete.setIsOpen(true);
    autocomplete.refresh();
  };

  const onClose: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    autocomplete.setIsOpen(false);
  };

  const onKeyPressHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key == "Enter") {
      e.preventDefault();
      requestInputOpen();
    }
  };

  const currentPlaceHolder = (state: InputState) =>
    match(state)
      .with("focused", () => typeHere)
      .with("empty", () => props.emptyPlaceholder ?? inputProps.placeholder)
      .with("full", () => undefined)
      .exhaustive();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputProps = autocomplete.getInputProps({
    inputElement: inputRef.current
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { placeholder, ...inputPropsRest } = inputProps;
  const inputState = currentInputState(hasTags, focused);

  const inputPlaceholder = currentPlaceHolder(inputState);
  useEnableMobileKeyboardUX({
    domRefs: { formRef, inputRef, containerRef, panelRef },
    isAutocompleteOpen: autocompleteState.isOpen,
    getEnvironmentProps: autocomplete.getEnvironmentProps
  });

  const darkVariant = backgroundColor == "titanium-dark";
  const autocompleteQuery = autocompleteState.query;
  useNoResultsTracking(noResults, search.tags, autocompleteState);

  const searchIsNotEmpty = (search: string | undefined) =>
    isPresent(search) && search.trim() !== "";

  return (
    <div
      className={clsx(className, styles.container, {
        [styles.detachedMode]: detachedMode,
        [styles.darkVariant]: backgroundColor == "titanium-dark",
        [styles.whiteVariant]: backgroundColor == "white"
      })}
      data-input-state={inputState}
      {...autocomplete.getRootProps({})}
      data-content-name={dataContentName}
      onClick={() => requestInputOpen()}
      onKeyDown={onKeyPressHandler}
      role="button"
      tabIndex={0}
    >
      <form
        ref={formRef}
        className={clsx(styles.formInput, {
          [styles.formInputWithTags]:
            search.tags.length > 0 || searchIsNotEmpty(inputRef.current?.value)
        })}
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
      >
        <label className={styles.inputLabel}>{props.placeholder}</label>
        <div className={styles.tagsAndInputWrapper}>
          <div className={styles.inputWrapperPrefix}>
            <Tags
              tags={search.tags}
              onRemoveTag={onRemoveTag}
              autocompleteProps={autocomplete.getListProps()}
              darkVariant={darkVariant}
              wrapContainer={detachedMode}
            >
              {detachedMode && (
                <input
                  className={styles.input}
                  ref={inputRef}
                  {...inputPropsRest}
                  placeholder={inputPlaceholder}
                  onKeyDown={onInputKeyDown}
                  onFocus={onInputFocus}
                  onBlur={onInputBlur}
                />
              )}
            </Tags>
          </div>
          {!detachedMode && (
            <div className={styles.inputWrapper}>
              <input
                className={styles.input}
                ref={inputRef}
                {...inputPropsRest}
                placeholder={inputPlaceholder}
                onKeyDown={onInputKeyDown}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
              />
            </div>
          )}
        </div>
      </form>
      {(autocompleteState.isOpen || detachedMode) && (
        <div
          ref={panelRef}
          style={{ zIndex: 999 }}
          className={[
            styles.panel,
            panelClassName
            //autocompleteState.status === "stalled" && "aa-Panel--stalled" // TODO: check if needed
          ]
            .filter(Boolean)
            .join(" ")}
          {...autocomplete.getPanelProps({})}
        >
          <div className={styles.panelContainer}>
            <div className={styles.scrollableContent}>
              {noResults && (
                <div className={styles.emptyResults}>
                  <h3>
                    <Trans id="autocomplete-no-results">
                      <p>
                        No results for <em>{autocompleteQuery}</em>
                      </p>
                      <p>
                        Try checking your spelling or try another destination,
                        country, city
                      </p>
                    </Trans>
                  </h3>
                </div>
              )}

              {autocompleteState.collections.map((collection, index) => {
                const { source, items } = collection;

                return (
                  <section
                    key={`source-${index}`}
                    className={styles.sectionContainer}
                  >
                    {items.length > 0 && (
                      <React.Fragment>
                        <div className={styles.sourceTitle}>
                          {i18n._(
                            labelForFacetSource(source.sourceId as FacetSource)
                          )}
                        </div>
                        <ul
                          className={styles.list}
                          {...autocomplete.getListProps()}
                        >
                          {items.map((item) => {
                            return (
                              <li
                                key={item.label}
                                {...autocomplete.getItemProps({ item, source })}
                              >
                                <BarCheckbox
                                  label={facetLabel(
                                    source.sourceId as FacetSource,
                                    item.label
                                  )}
                                  counterLabel={
                                    item.count > 0
                                      ? cruisesLabel(item.count).toLowerCase()
                                      : undefined
                                  }
                                  disabled={item.count <= 0}
                                  variant={darkVariant ? "titanium" : "gray"}
                                />
                              </li>
                            );
                          })}
                        </ul>
                      </React.Fragment>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
          <div className={styles.close}>
            <Button
              variant="text"
              renderAs="button"
              onClick={onClose}
              dataContentPiece="close"
              inverted={darkVariant}
            >
              <Trans>Close</Trans>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const useSourceRefinementList = (
  args: Omit<UseRefinementListProps, "attribute"> & { attribute: FacetSource }
) => useRefinementList(args);

const labelForFacetSource = (source: FacetSource) =>
  match(unlocalizedFacetSource<FacetSource, PublicFacetSource>(source))
    .with("countryNames", () => msg`Select the countries`)
    .with("destinationNames", () => msg`Select the destinations`)
    .with("portNames", () => msg`Select the cities`)
    .with("cruiseTagsTree.level1", () => msg`Select the cruise event or themes`)
    .exhaustive();

const currentInputState = (hasTags: boolean, isFocused: boolean): InputState =>
  match({ hasTags, isFocused })
    .with({ isFocused: true, hasTags: P._ }, () => "focused" as const)
    .with({ isFocused: false, hasTags: true }, () => "full" as const)
    .with({ isFocused: false, hasTags: false }, () => "empty" as const)
    .exhaustive();
